import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

/**
 * Loads the avatar catalog + the viewer's owned pool, and exposes the
 * RPCs that mutate them. Three pieces of state:
 *
 *   catalog — array of { slug, name, image_url, rarity, sort_order }
 *             from avatar_catalog. Public-read; small (a dozen rows
 *             for a long time), loaded once.
 *   owned   — Set<slug> of avatars the caller owns (user_avatars rows
 *             for auth.uid()). Refreshed after any grant / unlock.
 *   ready   — true once both loads settle.
 *
 * RPC wrappers:
 *   grantStarters() — one-shot starter pack grant (8 random commons).
 *                     Safe to call on every mount; server short-
 *                     circuits if the user already owns anything.
 *                     Returns the active { slug, url } for local
 *                     profile-state patching.
 *   pin(slug)       — switches the user's current avatar. Server
 *                     validates ownership and rejects unowned slugs.
 */
// Avatar catalog is effectively static — only changes via migration.
// 7-day TTL is safe; catalog additions land with new releases.
// owned-avatars TTL is shorter (12h) since users grant/unlock during
// gameplay.
//
// Cache version: bump on any migration that adds to avatar_catalog or
// grants to user_avatars (e.g. 0150 added 'trevi' + granted to all
// users). Old cached rows live up to 7 days so a key bump is the only
// way to surface the new state without forcing a manual storage clear.
const CATALOG_KEY = "avatars.catalog.v2";
const CATALOG_TTL = 7 * 24 * 60 * 60 * 1000;
const OWNED_KEY   = (uid) => `avatars.owned.v2:${uid}`;
const OWNED_TTL   = 12 * 60 * 60 * 1000;

export function useAvatars(userId) {
  const cachedCatalog = readCache(CATALOG_KEY, CATALOG_TTL);
  const cachedOwned   = userId ? readCache(OWNED_KEY(userId), OWNED_TTL) : null;
  const [catalog, setCatalog] = useState(() => cachedCatalog || []);
  const [owned, setOwned] = useState(() => new Set(cachedOwned || []));
  const [catalogLoading, setCatalogLoading] = useState(() => cachedCatalog === null);
  const [ownedLoading, setOwnedLoading] = useState(() => userId ? cachedOwned === null : false);

  // Catalog is public-read and changes only via migration. Cache for
  // 7 days; the network fetch only fires on cache miss / expiry.
  useEffect(() => {
    if (cacheFresh(CATALOG_KEY, CATALOG_TTL)) {
      setCatalogLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      const { data, error } = await supabase
        .from("avatar_catalog")
        .select("slug,name,image_url,rarity,sort_order")
        .order("sort_order", { ascending: true });
      if (!cancelled) {
        if (error) console.error("[avatars] catalog load:", error);
        const list = data || [];
        setCatalog(list);
        writeCache(CATALOG_KEY, list);
        setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadOwned = useCallback(async () => {
    if (!userId) {
      setOwned(new Set());
      setOwnedLoading(false);
      return;
    }
    setOwnedLoading(true);
    const { data, error } = await supabase
      .from("user_avatars")
      .select("slug")
      .eq("user_id", userId);
    if (error) console.error("[avatars] owned load:", error);
    const slugs = (data || []).map(r => r.slug);
    setOwned(new Set(slugs));
    writeCache(OWNED_KEY(userId), slugs);
    setOwnedLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setOwnedLoading(false);
      return;
    }
    if (cacheFresh(OWNED_KEY(userId), OWNED_TTL)) {
      setOwnedLoading(false);
      return;
    }
    loadOwned();
  }, [userId, loadOwned]);

  const grantStarters = useCallback(async () => {
    if (!userId) return null;
    const { data, error } = await supabase.rpc("grant_starter_avatars");
    if (error) {
      console.error("[avatars] grant_starters:", error);
      return null;
    }
    await loadOwned();
    const row = Array.isArray(data) ? data[0] : data;
    return row ? { slug: row.avatar_slug, url: row.avatar_url } : null;
  }, [userId, loadOwned]);

  const pin = useCallback(async (slug) => {
    if (!userId || !slug) return;
    const { error } = await supabase.rpc("set_avatar", { p_slug: slug });
    if (error) {
      console.error("[avatars] pin:", error);
      throw error;
    }
  }, [userId]);

  return {
    catalog,
    owned,
    ready: !catalogLoading && !ownedLoading,
    grantStarters,
    pin,
    refreshOwned: loadOwned,
  };
}
